import {type MouseEvent, memo} from 'react';
import {Pencil, Trash2} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {Button} from '../button';
import {cn} from '../lib/cn';
import {Switch} from '../switch';

export type SmallGridSingleItemProps = {
  title?: string;
  subtitle?: string;
  active?: boolean;
  onActiveSwitch?: (active: boolean) => void;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  onEdit?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  className?: string;
};

export const SmallGridSingleItem = memo(
  ({
    title,
    subtitle,
    active = false,
    onActiveSwitch,
    onDelete,
    onEdit,
    onClick,
    disabled,
    readOnly,
    error,
    className,
  }: SmallGridSingleItemProps) => {
    const {t} = useTranslation();

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (!disabled && onClick) onClick(event);
    };

    const hasActions = !readOnly && (onDelete || onEdit);
    const hasSwitch = onActiveSwitch && !readOnly;

    return (
      <div
        onClick={handleClick}
        className={cn(
          'relative flex h-full w-full shrink-0 flex-col gap-3',
          'box-border rounded-md px-5 py-4 text-left text-base font-semibold text-[var(--chekin-color-brand-navy)]',
          'shadow-[0_0_10px_rgba(111,194,255,0.2)]',
          disabled && 'cursor-not-allowed',
          !disabled && onClick && 'cursor-pointer',
          !disabled && !onClick && 'cursor-default',
          className,
        )}
      >
        <div className="flex w-full items-start justify-between gap-4">
          <div className="flex-1">
            <div className="line-clamp-2 overflow-hidden text-ellipsis break-all">
              {title}
            </div>
            {subtitle && (
              <div className="line-clamp-2 overflow-hidden text-ellipsis text-[15px] font-medium leading-6 text-[var(--chekin-color-gray-2)]">
                {subtitle}
              </div>
            )}
          </div>
          {hasActions && (
            <div className="flex items-center justify-end gap-2">
              {onDelete && (
                <Button
                  disabled={disabled}
                  onClick={onDelete}
                  className="size-8"
                  size="icon"
                  variant="outline"
                >
                  <Trash2 className="h-5 w-5 text-[var(--chekin-color-brand-red)]" />
                </Button>
              )}
              {onEdit && (
                <Button
                  className="size-8"
                  size="icon"
                  disabled={disabled}
                  onClick={onEdit}
                  variant="outline"
                >
                  <Pencil className="h-5 w-5 text-[var(--chekin-color-brand-blue)]" />
                </Button>
              )}
            </div>
          )}
        </div>

        {hasSwitch && (
          <div className={cn('flex items-center', !active && 'opacity-60')}>
            <Switch
              onChange={onActiveSwitch}
              disabled={disabled}
              value={active}
              label={t('active')}
              onClick={event => event.stopPropagation()}
            />
          </div>
        )}

        {error && (
          <div className="absolute bottom-1 right-2 text-xs text-[var(--chekin-color-brand-red)]">
            {error}
          </div>
        )}
      </div>
    );
  },
);

SmallGridSingleItem.displayName = 'SmallGridSingleItem';
